import VideoActionButtons, { type NotationMode } from "@/components/VideoActionsButton";
import {
  lessonNavForeground,
  lessonNavToolbarPillSurface,
} from "@/components/lesson/lessonNavButtonStyles";
import { MarkCompleteButton } from "@/components/lesson/MarkCompleteButton";
import { useAuth } from "@/lib/AuthContext";
import { resolveUserHomeUrl } from "@/lib/userHomeUrl";
import { useRouter } from "expo-router";
import React, { useMemo, useState } from "react";
import { Platform, Pressable } from "react-native";
import { SizableText, XStack, useMedia } from "tamagui";
import BreadcrumbChevronIcon from "@/assets/images/icons/breadcrumb-chevron.svg";
import BreadcrumbHomeIcon from "@/assets/images/icons/breadcrumb-home.svg";
import { Ionicons } from "@expo/vector-icons";

type Props = {
  courseTitle: string | null;
  courseSlug: string;
  seasonTitle?: string | null;
  lessonTitle?: string | null;
  canNavigatePrevious: boolean;
  canNavigateNext: boolean;
  isCompleted: boolean;
  completionLoading: boolean;
  isFiftyPercentWatched: boolean;
  onMarkComplete: () => void;
  notationMode: NotationMode;
  hasNotation: boolean | null;
  onNotationChange: (m: NotationMode) => void;
  onPreviousPress?: () => void;
  onNextPress?: () => void;
};

function LessonPageTopBarImpl({
  courseTitle,
  courseSlug,
  seasonTitle,
  lessonTitle,
  canNavigatePrevious,
  canNavigateNext,
  isCompleted,
  completionLoading,
  isFiftyPercentWatched,
  onMarkComplete,
  notationMode,
  hasNotation,
  onNotationChange,
  onPreviousPress,
  onNextPress,
}: Props) {
  const router = useRouter();
  const { preferenceMetadata, membershipTier, membershipLoading } = useAuth();
  const media = useMedia();
  const [hoverCourse, setHoverCourse] = useState(false);
  const [prevHover, setPrevHover] = useState(false);
  const [prevPress, setPrevPress] = useState(false);
  const [nextHover, setNextHover] = useState(false);
  const [nextPress, setNextPress] = useState(false);

  const homeUrl = useMemo(
    () =>
      resolveUserHomeUrl(
        preferenceMetadata ?? undefined,
        membershipTier,
        membershipLoading,
      ),
    [preferenceMetadata, membershipTier, membershipLoading],
  );

  const goHome = () => {
    if (homeUrl.startsWith("http://") || homeUrl.startsWith("https://")) {
      window.location.href = homeUrl;
      return;
    }
    router.replace(homeUrl as never);
  };

  const hasPrevious = canNavigatePrevious;
  const hasNext = canNavigateNext;

  const prevElevated = hasPrevious && (prevHover || prevPress);
  const nextElevated = hasNext && (nextHover || nextPress);
  const prevFg = lessonNavForeground(hasPrevious, prevElevated);
  const nextFg = lessonNavForeground(hasNext, nextElevated);

  if (Platform.OS !== "web") return null;

  const goCourse = () => {
    router.replace({
      pathname: "/courses/[course-slug]",
      params: { "course-slug": String(courseSlug) },
    });
  };

  return (
    <XStack
      w="100%"
      ai="flex-start"
      py="$2"
      px="$2"
      gap="$2"
      flexWrap="wrap"
      $gtSm={{ py: "$2.5" }}
    >
      <XStack ai="center" height={40} gap={0} flexShrink={1} minWidth={0}>
        <Pressable
          onPress={goHome}
          accessibilityRole="button"
          accessibilityLabel="Home"
          style={{ cursor: "pointer", padding: 4, marginRight: 8 }}
        >
          <BreadcrumbHomeIcon width={17} height={19} />
        </Pressable>

        <XStack marginRight={16}>
          <BreadcrumbChevronIcon width={6} height={10} />
        </XStack>

        {courseTitle ? (
          <Pressable
            onPress={goCourse}
            onHoverIn={() => setHoverCourse(true)}
            onHoverOut={() => setHoverCourse(false)}
            style={{
              cursor: "pointer",
              flexShrink: 1,
              minWidth: 0,
              marginRight: lessonTitle ? 8 : 0,
            }}
          >
            <SizableText
              size={media.gtSm ? "$4" : "$3"}
              $gtSm={{ size: "$5" }}
              fontWeight="500"
              color={hoverCourse ? "#795C40" : "#2B2B2B"}
              numberOfLines={1}
            >
              {courseTitle}
            </SizableText>
          </Pressable>
        ) : null}

        {lessonTitle ? (
          <>
            <XStack marginRight={16}>
              <BreadcrumbChevronIcon width={6} height={10} />
            </XStack>
            <SizableText
              size={media.gtSm ? "$4" : "$3"}
              $gtSm={{ size: "$5" }}
              fontWeight="500"
              color="#2B2B2B"
              numberOfLines={1}
              flexShrink={1}
            >
              {lessonTitle}
            </SizableText>
          </>
        ) : null}
      </XStack>

      <XStack ai="center" height={40} gap="$2" flexShrink={0} marginLeft="auto">
        <Pressable
          disabled={!hasPrevious}
          onPress={() => {
            if (onPreviousPress) onPreviousPress();
          }}
          onHoverIn={() => hasPrevious && setPrevHover(true)}
          onHoverOut={() => setPrevHover(false)}
          onPressIn={() => hasPrevious && setPrevPress(true)}
          onPressOut={() => setPrevPress(false)}
          style={[
            lessonNavToolbarPillSurface(hasPrevious, prevElevated, "previous"),
            hasPrevious ? { cursor: "pointer" as const } : null,
          ]}
          accessibilityRole="button"
          accessibilityLabel="Previous"
        >
          <Ionicons name="chevron-back" size={16} color={prevFg} />
          <SizableText fontSize={14} fontWeight="600" color={prevFg}>
            Previous
          </SizableText>
        </Pressable>

        <MarkCompleteButton
          isCompleted={isCompleted}
          isLoading={completionLoading}
          isFiftyPercentWatched={isFiftyPercentWatched}
          onPress={onMarkComplete}
          isMobile={false}
          isActive
          variant="toolbar"
        />

        <Pressable
          disabled={!hasNext}
          onPress={() => {
            if (onNextPress) onNextPress();
          }}
          onHoverIn={() => hasNext && setNextHover(true)}
          onHoverOut={() => setNextHover(false)}
          onPressIn={() => hasNext && setNextPress(true)}
          onPressOut={() => setNextPress(false)}
          style={[
            lessonNavToolbarPillSurface(hasNext, nextElevated, "next"),
            hasNext ? { cursor: "pointer" as const } : null,
          ]}
          accessibilityRole="button"
          accessibilityLabel="Next"
        >
          <SizableText fontSize={14} fontWeight="600" color={nextFg}>
            Next
          </SizableText>
          <Ionicons name="chevron-forward" size={16} color={nextFg} />
        </Pressable>

        <VideoActionButtons
          notationMode={notationMode}
          hasNotation={hasNotation}
          onNotationChange={onNotationChange}
          toolbarCompact
          reserveSpace
        />
      </XStack>
    </XStack>
  );
}

export const LessonPageTopBar = React.memo(LessonPageTopBarImpl);
